import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Container,
  styled,
  Box,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormTextField from "components/form/FormTextField";
import FormSelect from "components/form/FormSelect";
import StyledButton from "components/button/StyledButton";
import { useNavigate } from "react-router-dom";
import { ProjectContext } from "context/ProjectContext";
import "../../components/button/StyledButton.css";
import { useCreateProjectMutation, useGetLcaProjectPhasesQuery, useGetLcaToolsQuery, useGetUserQuery, useUpdateProjectMutation } from "api/api";
import { Project } from "types/types";
import { useCreateUpdateProject } from "hooks/useCreateUpdateProject";

const createSchema = (thirdPartyUsed: boolean) => z.object({
  other_lca_tool: thirdPartyUsed
    ? z.string({ required_error: "Third Party Embodied Carbon Assessment Tool is required", invalid_type_error: "Third Party Embodied Carbon Assessment Tool is required" })
    : z.null(),
  lca_year: thirdPartyUsed
    ? z.number({ invalid_type_error: "The year the LCA was performed is required" }).int().min(2000).max(2050).or(z.string().regex(/^\d{4}$/, "Must be a valid year"))
    : z.null(),
  lca_project_phase: z.string({ required_error: "Project Phase is required", invalid_type_error: "Project Phase is required" }),
  ref_study_years: z.number({ invalid_type_error: "The Reference Study Period is required" }).positive().int().or(z.string().regex(/^\d+$/, "Must be a positive integer")).transform(Number),
});

const AddProjectEmbodiedCarbonAssessment = () => {
  const navigate = useNavigate();
  const { data: lcaProjectPhases = [] } = useGetLcaProjectPhasesQuery();
  const { data: lcaTools = [] } = useGetLcaToolsQuery();
  const { data: userData } = useGetUserQuery();

  const listLcaProjectPhases = lcaProjectPhases.map(item => item.name);
  const listLcaTools = lcaTools.map(item => item.name);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2000 + 1 }, (_, index) => 2000 + index);

  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('ProjectContext error');
  }
  const { projectDetails, setProjectDetails } = context;

  const [thirdPartyUsed, setThirdPartyUsed] = useState(!!projectDetails["other_lca_tool"] || !!projectDetails["lca_year"]);

  const schema = useMemo(() => createSchema(thirdPartyUsed), [thirdPartyUsed]);


  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      other_lca_tool: projectDetails.other_lca_tool || null,
      lca_year: projectDetails.lca_year || null,
      lca_project_phase: projectDetails.lca_project_phase || null,
      ref_study_years: projectDetails.ref_study_years || null,
    },
  });

  const { control, handleSubmit, getValues, setValue, formState: { errors, isSubmitting } } = form;

  const { handleCreateUpdateProject } = useCreateUpdateProject({ userData })

  const handleGoToNextForm = async () => {
    setProjectDetails(prevDetails => ({
      ...prevDetails,
      ...getValues(),
    }));
    handleCreateUpdateProject({ formValues: getValues(), leaveForm: false })
    navigate('/add-project/8');
  };

  const handleSaveAndExit = async () => {
    handleCreateUpdateProject({})
  };

  const handleClickBack = () => {
    setProjectDetails(prevDetails => ({
      ...prevDetails,
      ...getValues(),
    }));
    navigate('/add-project/6');
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    if (!isChecked) {
      setValue("other_lca_tool", null);
      setValue("lca_year", null);
    }
    setThirdPartyUsed(isChecked);
  };

  const renderFormFields = () => {
    const fields = thirdPartyUsed
      ? [
        { name: "other_lca_tool", label: "Third Party Embodied Carbon Assessment Tool", value: projectDetails["other_lca_tool"], component: "FormSelect", menuItems: listLcaTools },
        { name: "lca_year", label: "Year Third Party Assessment was Performed", value: projectDetails["lca_year"], component: "FormSelect", menuItems: years },
        { name: "lca_project_phase", label: "Project Phase", value: projectDetails["lca_project_phase"], component: "FormSelect", menuItems: listLcaProjectPhases },
        { name: "ref_study_years", label: "Reference Study Period", value: projectDetails["ref_study_years"], component: "FormTextField" },
      ]
      : [
        { name: "lca_project_phase", label: "Project Phase", value: projectDetails["lca_project_phase"], component: "FormSelect", menuItems: listLcaProjectPhases },
        { name: "ref_study_years", label: "Reference Study Period", value: projectDetails["ref_study_years"], component: "FormTextField" },
      ];

    return fields.map((inputLabel) => (
      <Controller
        key={inputLabel.name}
        name={inputLabel.name as any}
        control={control}
        defaultValue={inputLabel.value}
        render={({ field }) => {
          const errorMessage = errors[inputLabel.name]?.message as string | undefined;
          return (
            <Box>
              {inputLabel.component === "FormSelect" ? (
                <FormSelect
                  {...field}
                  value={field.value || ""}
                  label={inputLabel.label}
                  error={!!errors[inputLabel.name]}
                  helperText={errorMessage}
                  menuItems={inputLabel.menuItems}
                  disabled={isSubmitting}
                />
              ) : (
                <FormTextField
                  {...field}
                  value={field.value || ""}
                  label={inputLabel.label}
                  error={!!errors[inputLabel.name]}
                  helperText={errorMessage}
                  disabled={isSubmitting}
                />
              )}
            </Box>
          );
        }}
      />
    ));
  };

  return (
    <StyledContainer>
      <div style={{ width: '100%', height: '97vh', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 41, paddingTop: 37 }}>
        <Box sx={{ width: "380px" }}>
          <div style={{ width: '100%', height: '100%', color: '#1A1A1A', fontSize: 28, fontFamily: 'Helvetica Neue LT Std', fontWeight: '300', wordWrap: 'break-word', paddingBottom: 12 }}>Embodied Carbon Assessment</div>
          <form onSubmit={handleSubmit(handleGoToNextForm)}>
            <Box sx={{ marginTop: '14px', marginBottom: '24px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="third-party-checkbox"
                    checked={thirdPartyUsed}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Check this box if you used a third party embodied carbon assessment tool to perform a Life Cycle Assessment on this project."
              />
            </Box>
            {renderFormFields()}
            <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start" }}>
              <StyledButton label="Save &amp; Exit" type="outlined" onClick={handleSubmit(handleSaveAndExit)} />
              <StyledButton label="Back" type="outlined" onClick={handleClickBack} />
              <StyledButton label="Next" type="filled" onClick={handleSubmit(handleGoToNextForm)} />
            </Box>
          </form>
        </Box>
      </div>
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)({
  marginBottom: 24,
});

export default AddProjectEmbodiedCarbonAssessment;